/**
 * AdminScheduling — control surface for the photographer worklist engine.
 *
 * Tunes the global scheduling settings the engine reads live: the business SLA
 * default, the balance weights (the "balanced" personality = aging gated by SLA,
 * then margin/efficiency), and the $ estimate inputs. Per-dealer SLA overrides
 * live on the Rate Cards page. Uses the admin (adminAuth) token pattern.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';

const FIELDS = [
  { key: 'sla_default_days', label: 'Business SLA (days to first photo)', step: 1, min: 1,
    help: 'A new car must be photographed within this many days. Per-dealer overrides on Rate Cards.' },
  { key: 'weight_age', label: 'Weight — Aging / urgency', step: 0.1, min: 0,
    help: 'How hard aging pushes a dealer up the list (overdue dealers are pinned regardless of weight).' },
  { key: 'weight_margin', label: 'Weight — Margin (our revenue)', step: 0.1, min: 0,
    help: 'Steers toward higher-bill dealers (bill − payout). Our thumb on the scale.' },
  { key: 'weight_payout', label: 'Weight — Payout volume (photographer $)', step: 0.1, min: 0,
    help: 'Rewards dealers with more payable work for the photographer.' },
  { key: 'default_payout_per_car', label: 'Default payout per car ($)', step: 0.5, min: 0,
    help: 'Flat photographer pay used for the "$ potential" estimate on the picker.' },
  { key: 'shoot_minutes_per_car', label: 'Shoot minutes per car', step: 1, min: 0,
    help: 'Used for time-to-complete / value-per-hour estimates.' },
  { key: 'active_inventory_days', label: 'Live inventory window (days)', step: 1, min: 1,
    help: 'Worklist counts only cars the feed touched within this window — auto-excludes paused dealers & stale cars; they return when their feed resumes.' },
];

export default function AdminScheduling() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [navigate]);

  const json = useCallback((r) => {
    if (r.status === 401 || r.status === 403) {
      localStorage.removeItem('adminAuth'); navigate('/admin/login');
      throw new Error('session expired');
    }
    return r.json();
  }, [navigate]);

  useEffect(() => {
    const h = authHeader();
    if (!h) return;
    fetch(`${API_BASE}/admin/scheduling-settings`, { headers: h })
      .then(json)
      .then(d => setS(d))
      .catch(e => setMsg({ type: 'err', text: e.message }))
      .finally(() => setLoading(false));
  }, [authHeader, json]);

  const onChange = (key, val) => setS(prev => ({ ...prev, [key]: val === '' ? '' : Number(val) }));

  const save = async () => {
    const h = authHeader();
    if (!h) return;
    setSaving(true); setMsg(null);
    try {
      const payload = {};
      FIELDS.forEach(f => { if (s[f.key] !== '' && s[f.key] != null) payload[f.key] = s[f.key]; });
      const d = await fetch(`${API_BASE}/admin/scheduling-settings`, {
        method: 'PUT', headers: h, body: JSON.stringify(payload),
      }).then(json);
      setS(d);
      setMsg({ type: 'ok', text: 'Saved — the engine uses these immediately.' });
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!s) return <div style={{ padding: 24 }}>Could not load settings.</div>;

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <h1 style={{ margin: '0 0 4px' }}>Photographer Scheduling</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        Tunes the worklist engine. <strong>Balanced</strong> = aging gated by SLA (overdue dealers
        pinned), then margin &amp; efficiency. Changes apply immediately.
      </p>

      <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, padding: 20 }}>
        {FIELDS.map(f => (
          <div key={f.key} style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>{f.label}</label>
            <input
              type="number" step={f.step} min={f.min}
              value={s[f.key] ?? ''}
              onChange={e => onChange(f.key, e.target.value)}
              style={{ width: 160, padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 15 }}
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>{f.help}</div>
          </div>
        ))}

        <button
          onClick={save} disabled={saving}
          style={{ background: '#0a7', color: '#fff', border: 0, borderRadius: 6,
                   padding: '10px 22px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                   opacity: saving ? 0.6 : 1 }}
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        {msg && (
          <span style={{ marginLeft: 14, color: msg.type === 'ok' ? '#0a7' : '#c00', fontWeight: 600 }}>
            {msg.text}
          </span>
        )}
      </div>
    </div>
  );
}
